import {
  Alert,
  Button,
  Checkbox,
  Col,
  Descriptions,
  Form,
  Input,
  List,
  Popconfirm,
  Row,
  Space,
  Tag,
  Typography,
  theme,
} from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  CalendarInvitation,
  EventParticipantRole,
  Training,
  TrainingStatus,
} from "../../repository/data.default";
import {
  updateTraining,
  completeTraining,
  cancelTraining,
  setTrainingAttendance,
  getCalendarInvitations,
  getCompanyCollaborators,
  addCalendarUserInvitations,
} from "../../repository/data.repository";
import { trainingStatusColor } from "./TrainingsView.default";
import InModal from "../../controls/InModal";
import DynamicForm from "../../controls/DynamicForm";
import ComboBox from "../../controls/ComboBox";
import { InputObject } from "../../controls/default";
import ManageStatusBanner from "../../controls/ManageStatusBanner";
import { useDialogAction } from "../../hooks/useDialogAction";
import { createScopedSearchRequest } from "../../hooks/useSearchRequest";
import { fmtDateTime as fmt, dateTimeFormat } from "../../utils/date";
import { nameOf } from "../../utils/typescript";
import { getProblemMessage } from "../../utils/http";
import { QueryOperation } from "../../code/search-query/search-query";
import {
  addEventInvitations,
  ParticipantItem,
  participantTagColor,
} from "../calendar/calendarEventForm";
import EventParticipantsPanel from "../calendar/EventParticipantsPanel";
import ParticipantTargetSelect from "../calendar/ParticipantTargetSelect";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";

export interface TrainingManageDialogProps {
  readonly open?: boolean;
  readonly record?: Training;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
  // Deep-link to the backing calendar event.
  readonly onViewEvent?: (eventUid: string) => void;
}

// State → one-line guidance: where the training stands and the next move.
const guidanceFor = (status?: TrainingStatus, started?: boolean): string => {
  switch (status) {
    case TrainingStatus.Planned:
      return started
        ? "The session has started (or passed) — mark it completed and record who attended."
        : "The session is scheduled — manage its details and invite the participants below.";
    case TrainingStatus.Completed:
      return "This training has been completed — attendance can still be corrected below.";
    case TrainingStatus.Cancelled:
      return "This training has been cancelled.";
    default:
      return "";
  }
};

/**
 * Single purpose-built surface for a training once it exists: details are editable here while it is
 * planned, participants are invited against the backing calendar event, and the lifecycle (complete
 * with an attendance checklist / cancel) lives in the same view, gated by the current state.
 */
function TrainingManageDialog(props: TrainingManageDialogProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const { loading, setLoading, errorMessage, setErrorMessage, run } =
    useDialogAction({ onSuccess: props.onFinish });
  // Bumps the participants panel + the invite list after an invite/remove.
  const [refreshKey, setRefreshKey] = useState(0);
  // The backing event's invites — the attendance checklist rows.
  const [invites, setInvites] = useState<CalendarInvitation[]>([]);
  // Server total of the event's invites — when it exceeds the loaded page, the checklist says so.
  const [inviteTotal, setInviteTotal] = useState(0);
  // Draft attendance marks by invite uid (seeded from the persisted marks).
  const [attendanceDraft, setAttendanceDraft] = useState<Record<string, boolean>>({});
  // While planned + started, "Mark Completed" reveals the checklist before confirming.
  const [completing, setCompleting] = useState(false);
  // PD-01 §10 — host's post-training write-up, captured during Mark Completed
  // and persisted via /Trainings/{uid}/Complete alongside attendance.
  const [feedback, setFeedback] = useState("");
  // Remount key for the co-trainer picker — bumped after every pick so the control clears.
  const [coTrainerPickKey, setCoTrainerPickKey] = useState(0);

  const permissions = useSelector(authorizeSelectors.permissions);
  const isAdmin = useSelector(authorizeSelectors.isAppAdmin);
  // Read-only managers can open the view; only hosts/admins act (the server enforces it too).
  const canEdit = permissions.can("Trainings", "edit");

  const status = props.record?.status;
  const isPlanned = status === TrainingStatus.Planned;
  const isCompleted = status === TrainingStatus.Completed;
  const isCancelled = status === TrainingStatus.Cancelled;
  const started =
    !!props.record?.startDate && !dayjs(props.record.startDate).isAfter(dayjs());
  const canComplete = isPlanned && started && canEdit;
  // The checklist is up whenever attendance is being recorded or corrected.
  const showAttendance = (completing && canComplete) || isCompleted;

  useEffect(() => {
    if (props.open) {
      setErrorMessage("");
      setLoading(false);
      setCompleting(false);
      setRefreshKey(0);
      form.setFieldsValue({
        title: props.record?.title,
        description: props.record?.description,
        location: props.record?.location,
        startDate: props.record?.startDate
          ? dayjs(props.record.startDate)
          : undefined,
        endDate: props.record?.endDate ? dayjs(props.record.endDate) : undefined,
        host: props.record?.hostUid
          ? {
              display: props.record.hostUserName,
              value: props.record.hostUid,
            }
          : undefined,
      });
    } else {
      form.resetFields();
      setInvites([]);
      setInviteTotal(0);
      setAttendanceDraft({});
    }
  }, [props.open]);

  // The backing event's invites, for the attendance checklist (and its persisted marks). The
  // checklist loads the first page only — the total is kept so an overflow is called out, and
  // completion sends marks for the loaded invites only.
  useEffect(() => {
    const eventUid = props.record?.eventUid;
    if (!props.open || !eventUid) return;

    let cancelled = false;
    getCalendarInvitations({
      searchQuery: {
        conditions: [
          {
            field: nameOf<CalendarInvitation>("eventUid"),
            operation: QueryOperation.Equal,
            values: [eventUid],
          },
        ],
      },
      pageNumber: 0,
      pageSize: 200,
      sortBy: nameOf<CalendarInvitation>("createdOn"),
      sortDescending: false,
    })
      .then((result) => {
        if (cancelled) return;
        setInvites(result.data);
        setInviteTotal(result.totalRecords);
        setAttendanceDraft(
          Object.fromEntries(result.data.map((i) => [i.uid, i.attended ?? false])),
        );
      })
      .catch((err) => {
        if (!cancelled) setErrorMessage(getProblemMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [props.open, props.record?.eventUid, refreshKey]);

  function onSaveDetails(): void {
    if (!props.record) return;
    const { title, description, location, startDate, endDate, host } =
      form.getFieldsValue();
    run(() =>
      updateTraining(props.record!.uid, {
        title,
        description,
        location,
        startDate: dayjs(startDate).toDate(),
        endDate: dayjs(endDate).toDate(),
        // Changing the host is admin-only; everyone else echoes the unchanged host back.
        hostUid: host?.value ?? props.record!.hostUid,
        rowVersion: props.record!.rowVersion,
      }),
    );
  }

  function onConfirmCompletion(): void {
    if (!props.record) return;
    run(() =>
      completeTraining(props.record!.uid, {
        attendance: invites.map((invite) => ({
          inviteUid: invite.uid,
          attended: !!attendanceDraft[invite.uid],
        })),
        feedback: feedback.trim() || undefined,
      } as any),
    );
  }

  function onCancelTraining(): void {
    if (!props.record) return;
    run(() => cancelTraining(props.record!.uid));
  }

  // Post-completion corrections save immediately, one mark at a time, without closing the view.
  function onToggleAttendance(invite: CalendarInvitation, attended: boolean): void {
    setAttendanceDraft((prev) => ({ ...prev, [invite.uid]: attended }));
    if (!isCompleted || !props.record) return;

    setLoading(true);
    setErrorMessage("");
    setTrainingAttendance(props.record.uid, {
      items: [{ inviteUid: invite.uid, attended }],
    })
      .then(() => {
        setLoading(false);
        setInvites((prev) =>
          prev.map((i) =>
            i.uid === invite.uid
              ? {
                  ...i,
                  attended,
                  attendedAt: attended ? dayjs().toISOString() : undefined,
                }
              : i,
          ),
        );
      })
      .catch((err) => {
        setLoading(false);
        setErrorMessage(getProblemMessage(err));
        // Roll the draft back so the checkbox reflects the persisted mark.
        setAttendanceDraft((prev) => ({ ...prev, [invite.uid]: !attended }));
      });
  }

  // Invite a freshly-picked target (user / department / whole company) immediately — the fan-out picks
  // the right bulk endpoint and the panel + checklist refetch via refreshKey.
  function onPickParticipant(picked: ParticipantItem): void {
    if (!picked?.value) return;

    const eventUid = props.record?.eventUid;
    if (!eventUid) return;
    setLoading(true);
    setErrorMessage("");
    addEventInvitations(eventUid, [picked])
      .then(() => {
        setLoading(false);
        setRefreshKey((k) => k + 1);
      })
      .catch((err) => {
        setLoading(false);
        setErrorMessage(getProblemMessage(err));
      });
  }

  // Add a co-trainer: a Host invite on the backing event (the server admits only affiliates assigned
  // to the training's company). Mirrors the attendee pick flow — invite immediately, refetch via
  // refreshKey, clear the picker.
  function onPickCoTrainer(picked?: InputObject): void {
    if (!picked?.value) return;

    const eventUid = props.record?.eventUid;
    if (!eventUid) return;
    setLoading(true);
    setErrorMessage("");
    addCalendarUserInvitations({
      eventUid,
      userUid: picked.value,
      role: EventParticipantRole.Host,
    })
      .then(() => {
        setLoading(false);
        setCoTrainerPickKey((k) => k + 1);
        setRefreshKey((k) => k + 1);
      })
      .catch((err) => {
        setLoading(false);
        setCoTrainerPickKey((k) => k + 1);
        setErrorMessage(getProblemMessage(err));
      });
  }

  // Methods

  // The training is tied to a company — targets are that company's members + assigned affiliates,
  // its departments, or the whole company, via the same picker the calendar event dialogs use.
  const trainingCompanyUid = props.record?.companyUid;

  const detailsEditable = isPlanned && canEdit;

  return (
    <InModal
      open={props.open}
      size="large"
      title="Manage Training"
      onCancel={props.onCancel}
      actions={
        <Space>
          <Button disabled={loading} onClick={props.onCancel}>
            Close
          </Button>
          {props.record?.eventUid && props.onViewEvent && !isCancelled && (
            <Button onClick={() => props.onViewEvent!(props.record!.eventUid!)}>
              View on calendar
            </Button>
          )}
          {detailsEditable && (
            <Button loading={loading} onClick={onSaveDetails}>
              Save Details
            </Button>
          )}
          {canComplete && !completing && (
            <Button
              type="primary"
              loading={loading}
              onClick={() => {
                // Re-fetch the invites so the checklist reflects any panel-side changes.
                setRefreshKey((k) => k + 1);
                setCompleting(true);
              }}
            >
              Mark Completed
            </Button>
          )}
          {canComplete && completing && (
            <Button type="primary" loading={loading} onClick={onConfirmCompletion}>
              Confirm Completion
            </Button>
          )}
          {detailsEditable && (
            <Popconfirm
              title="Cancel this training?"
              description="The calendar event and its invitations are removed and the invitees notified."
              okText="Cancel Training"
              cancelText="Keep"
              onConfirm={onCancelTraining}
            >
              <Button danger loading={loading}>
                Cancel Training
              </Button>
            </Popconfirm>
          )}
        </Space>
      }
    >
      {/* Status banner + contextual guidance */}
      <ManageStatusBanner
        tagColor={trainingStatusColor(status)}
        status={status}
        guidance={guidanceFor(status, started)}
      />

      <Row gutter={16} className="flex flex-row">
        {/* Details — editable while the training is planned, read-only once closed */}
        <Col flex="1 1 0" className="min-w-0">
          <Typography.Text strong className="mb-2 block">
            Details
          </Typography.Text>
          {detailsEditable ? (
            <>
              <Typography.Text type="secondary" className="mb-2 block text-xs">
                {`Company: ${props.record?.companyName ?? "—"}`}
              </Typography.Text>
              <DynamicForm
                form={form}
                schema={{
                  groups: [
                    {
                      controls: [
                        {
                          type: "textbox",
                          name: "title",
                          label: "Title",
                          rules: [
                            { required: true, message: "Title is required" },
                          ],
                        },
                        {
                          type: "areabox",
                          name: "description",
                          label: "Description",
                        },
                        {
                          type: "textbox",
                          name: "location",
                          label: "Location",
                        },
                        {
                          type: "datetimepicker",
                          name: "startDate",
                          label: "Starts",
                          format: dateTimeFormat,
                          rules: [
                            { required: true, message: "Start is required" },
                          ],
                        },
                        {
                          type: "datetimepicker",
                          name: "endDate",
                          label: "Ends",
                          format: dateTimeFormat,
                          rules: [{ required: true, message: "End is required" }],
                        },
                        {
                          type: "combobox",
                          name: "host",
                          label: "Lead trainer",
                          // Handing the training to another affiliate is admin-only.
                          readOnly: !isAdmin,
                          request: hostsForCompanyRequest(trainingCompanyUid),
                        },
                      ],
                    },
                  ],
                }}
              />
            </>
          ) : (
            <Descriptions
              size="small"
              column={1}
              bordered
              items={[
                {
                  key: "title",
                  label: "Title",
                  children: props.record?.title ?? "—",
                },
                {
                  key: "description",
                  label: "Description",
                  children: props.record?.description || "—",
                },
                {
                  key: "location",
                  label: "Location",
                  children: props.record?.location || "—",
                },
                {
                  key: "startDate",
                  label: "Starts",
                  children: fmt(props.record?.startDate),
                },
                {
                  key: "endDate",
                  label: "Ends",
                  children: fmt(props.record?.endDate),
                },
                {
                  key: "company",
                  label: "Company",
                  children: props.record?.companyName ?? "—",
                },
                {
                  key: "host",
                  label: "Lead trainer",
                  children: props.record?.hostUserName ?? "—",
                },
                {
                  key: "completedAt",
                  label: "Completed At",
                  children: fmt(props.record?.completedAt),
                },
              ]}
            />
          )}
        </Col>

        {/* Participants / attendance — invites against the backing event */}
        <Col flex="1 1 0" className="min-w-0">
          <Typography.Text strong className="mb-2 block">
            {showAttendance ? "Attendance" : "Participants"}
          </Typography.Text>

          {isCancelled && (
            <Typography.Text type="secondary">
              The backing event and its invitations were removed when the training
              was cancelled.
            </Typography.Text>
          )}

          {!isCancelled && showAttendance && (
            <>
              {canEdit && (
                <Typography.Text type="secondary" className="mb-1 block text-xs">
                  {completing
                    ? "Tick everyone who attended, then confirm the completion."
                    : "Corrections save immediately."}
                </Typography.Text>
              )}
              {inviteTotal > invites.length && (
                <Typography.Text type="warning" className="mb-1 block text-xs">
                  {`Showing the first ${invites.length} of ${inviteTotal} invitees — mark the rest after completing, from the participants list.`}
                </Typography.Text>
              )}
              <List
                style={{
                  backgroundColor: token.colorBgLayout,
                  borderRadius: token.borderRadius,
                }}
                dataSource={invites}
                locale={{ emptyText: "No participants were invited" }}
                renderItem={(invite) => (
                  <List.Item className="p-1 pl-2 pr-2">
                    <Checkbox
                      checked={!!attendanceDraft[invite.uid]}
                      disabled={loading || !canEdit}
                      onChange={(e) => onToggleAttendance(invite, e.target.checked)}
                    >
                      {invite.userUserName}
                    </Checkbox>
                    <Space>
                      <Tag color={participantTagColor(invite.role)} className="m-0">
                        {invite.role}
                      </Tag>
                      {isCompleted && (
                        <Tag
                          color={invite.attended ? "green" : "default"}
                          className="m-0"
                        >
                          {invite.attended ? "Attended" : "Absent"}
                        </Tag>
                      )}
                    </Space>
                  </List.Item>
                )}
              />

              {/* PD-01 §10 — post-training feedback. Writable while completing
                  (goes with the Confirm Completion call); read-only after. */}
              <div className="mt-3">
                <Typography.Text strong className="mb-1 block">
                  Host feedback
                </Typography.Text>
                {completing ? (
                  <Input.TextArea
                    rows={4}
                    value={feedback}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setFeedback(e.target.value)
                    }
                    placeholder="What was covered, standout moments, follow-up actions… Shown in Training Reports."
                    maxLength={2000}
                    showCount
                  />
                ) : (
                  <div
                    className="whitespace-pre-wrap rounded p-3"
                    style={{
                      background: token.colorFillQuaternary,
                      color: props.record?.feedback
                        ? token.colorText
                        : token.colorTextDescription,
                    }}
                  >
                    {props.record?.feedback || "No feedback captured yet."}
                  </div>
                )}
              </div>
            </>
          )}

          {!isCancelled && !showAttendance && (
            <>
              {detailsEditable && (
                <div className="flex w-full min-w-0 flex-col p-1">
                  <Typography.Text
                    className="text-sm"
                    style={{ color: token.colorTextDescription }}
                  >
                    Add co-trainer
                  </Typography.Text>
                  <ComboBox
                    key={coTrainerPickKey}
                    className="w-full"
                    placeholder="Search the company's assigned affiliates"
                    disabled={loading}
                    request={hostsForCompanyRequest(trainingCompanyUid)}
                    onChange={onPickCoTrainer}
                  />
                  <Typography.Text type="secondary" className="text-xs">
                    Co-trainers can manage, complete and mark attendance.
                  </Typography.Text>
                </div>
              )}
              {detailsEditable && (
                <ParticipantTargetSelect
                  className="w-full"
                  label="Add Participant"
                  companyUid={trainingCompanyUid}
                  disabled={loading}
                  onPick={onPickParticipant}
                />
              )}
              <EventParticipantsPanel
                eventUid={props.record?.eventUid}
                staged={[]}
                editable={detailsEditable}
                refreshKey={refreshKey}
                onRemoveStaged={() => undefined}
              />
            </>
          )}
        </Col>
      </Row>

      {errorMessage && (
        <Alert className="mt-3" message={errorMessage} type="error" />
      )}
    </InModal>
  );
}

// The host picker offers the training's company's assigned affiliates (consultants + collaborators),
// keyed by the underlying user uid (the CompanyCollaborators join row carries it).
function hostsForCompanyRequest(companyUid?: string) {
  return createScopedSearchRequest(
    getCompanyCollaborators,
    "userUserName",
    "companyUid",
    () => companyUid,
    undefined,
    { valueField: "userUid", subDisplayField: "companyName" },
  );
}

export default TrainingManageDialog;
